// Core Modules
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';

// External Modules
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import TimeAgo from 'javascript-time-ago';
import localeData from 'javascript-time-ago/locale/en.json';
import { ClipboardModule } from 'ngx-clipboard';
import { Subscription } from 'rxjs';

// Components
import { ListViewComponent } from '@shared/components/list-view/list-view.component';
import { PageTitleComponent } from '@shared/components/page-title/page-title.component';
import { ToastsContainerComponent } from '@shared/components/toasts/toasts-container.component';

// Services
import { ApiService } from '@shared/services/api/api.service';
import { ToastsService } from '@shared/services/toasts/toasts.service';

// Models
import { IToken, ITokenList } from '@shared/models/auth-api-response.model';
import { IPageTitleModel } from '@shared/models/page-title-config-model';
import { ITableListViewSettingsModel } from '@shared/models/table/list-view-config.model';

// Enums
import { MainNavigationEnum } from '@shared/enum/main-navigation-url.enum';

// Contents
import tokenConfigJson from '@tokens/tokens-contents.json';

TimeAgo.addLocale(localeData);
const timeAgo = new TimeAgo('en-US');

export class Token {
  id!: string;
  created!: Date;
  expires!: Date;

  get createdAgo(): string {
    return timeAgo.format(this.created);
  }

  get expiresAt(): string {
    if (!this.expires) {
      return 'never';
    }
    return timeAgo.format(this.expires);
  }
}

@Component({
  selector: 'app-tokens',
  templateUrl: './tokens.component.html',
  styleUrl: './tokens.component.scss',
  standalone: true,
  imports: [ClipboardModule, ListViewComponent, PageTitleComponent, ToastsContainerComponent],
})
export class TokensComponent implements OnInit, OnDestroy {
  // Bindings
  @ViewChild('create_modal') createModel!: TemplateRef<HTMLElement>;
  @ViewChild('delete_modal') deleteModel!: TemplateRef<HTMLElement>;

  // Injectors
  private readonly apiService = inject(ApiService);
  private readonly ngbModal = inject(NgbModal);
  private readonly toastsService = inject(ToastsService);

  // Private Members
  private readonly subscription = new Subscription();

  // Public Members
  public readonly tokenListViewHeaderDef: Record<string, object> = tokenConfigJson.listViewHeaderDef;
  public readonly tokenListViewSettings: ITableListViewSettingsModel = {
    ariaLabel: 'Token Management List',
    ariaLabelDesc: 'Token Management List',
    cssClass: ['token-management-list-view'],
    header: {
      name: {
        id: 'tm-tbl-th-token-name',
      },
    },
    row: {
      actions: [
        {
          id: 'more',
          type: 'button',
          cssClass: 'my-token-list-action-dd',
          value: 'Delete user',
          options: [
            {
              id: 'my-delete-token',
              permissionOnly: ['token-write'],
            },
          ],
        },
      ],
    },
  };
  public displayLoader: boolean = false;
  public modalToken: IToken = {} as IToken;
  public pageTitle?: IPageTitleModel;
  public tokens: Token[] = [];

  public ngOnInit(): void {
    this.setPageTitle();
    this.refreshList();
  }

  private setPageTitle(): void {
    this.pageTitle = {
      id: 'my-tokens-page-title-wrapper',
      showPageTitleBar: true,
      pageTitleWrapperClass: 'col-12 col-md-7 p-0 align-self-start text-start',
      pageTitleActionWrapperClass: 'ps-0 col-12 col-md-5 align-items-sm-start align-items-md-end',
      title: {
        label: 'Token Management',
        disableTooltip: true,
        id: 'tm-token-management-page-title-txt',
      },
      subTitle: {
        label: 'Allow users to view and create their own authenticated API Tokens.',
        disableTooltip: true,
        id: 'tm-token-management-page-sub-title-txt',
      },
      options: [
        {
          value: 'Create Token',
          type: 'button',
          id: 'tm-token-management-create-campaign',
          permissionScope: 'token-write',
        },
      ],
      backBtn: {
        stateName: `/${MainNavigationEnum.HOME}`,
        label: 'Home',
        id: 'back',
      },
    };
  }

  public callback(): void {
    this.onCreate(this.createModel);
  }

  public listCallBack(event: Record<string, any>): void {
    if (event.value === 'Delete user') {
      this.onDelete(this.deleteModel, event.data);
    }
  }

  public onCreate(content: TemplateRef<HTMLElement> | string): void {
    this.subscription.add(
      this.apiService.newToken().subscribe({
        next: (token: IToken) => {
          this.modalToken = token;
          const modal = this.ngbModal.open(content, { ariaLabelledBy: 'token-create-model' });
          modal.result
            .then(() => {
              this.refreshList();
              this.showSuccessToast('Token created successfully.');
            })
            .catch(() => {
              this.refreshList();
              this.showSuccessToast('Token created successfully.');
            });
        },
        error: (e: HttpErrorResponse) => {
          const errorMessage = e.error.message ? e.error.message : 'Error occurred during creating token';
          console.log('<MyTokenComponent> - <createToken> Error occurred while creating token...', errorMessage);
          this.showFailureToast(errorMessage);
        },
      })
    );
  }

  public onDelete(content: TemplateRef<HTMLElement> | string, token: Token): void {
    this.modalToken = token as any;
    const modal = this.ngbModal.open(content, { ariaLabelledBy: 'modal-basic-title' });
    modal.result.then(
      (result) => {
        if (result) {
          this.subscription.add(
            this.apiService.deleteToken(token.id).subscribe({
              next: () => {
                this.refreshList();
              },
              error: (e: HttpErrorResponse) => {
                const errorMessage = e.error.message ? e.error.message : 'Error occurred during deleting';
                console.log('<MyTokensComponent> - <onDelete> - Error occurred during deleting token', e);
                this.showFailureToast(errorMessage);
              },
              complete: () => {
                this.showSuccessToast('Token deleted successfully.');
              },
            })
          );
        }
      },
      () => {}
    );
  }

  public refreshList(): void {
    this.displayLoader = true;
    this.subscription.add(
      this.apiService.listTokens().subscribe({
        next: (data: ITokenList) => {
          this.displayLoader = false;
          this.tokens = data.items.map((item: IToken) => {
            return Object.assign(new Token(), {
              id: item.id,
              created: item.createDate ? new Date(Date.parse(item.createDate)) : null,
              expires: item.expires ? new Date(Date.parse(item.expires as string)) : null,
            });
          });
        },
        error: (error: HttpErrorResponse) => {
          this.displayLoader = false;
          console.log('<MyTokensComponent> - <refreshList> - Failed while fetching list tokens', error);
        },
      })
    );
  }

  public showFailureToast(message: string): void {
    this.toastsService.show(message, { class: 'ui-notification alert-panel warning text-dark', type: 'warning' });
  }

  public showSuccessToast(message: string): void {
    this.toastsService.show(message, { class: 'ui-notification alert-panel success text-dark', type: 'success' });
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.toastsService.clear();
  }
}
